import type { SVGProps } from 'react';

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> & {
  size?: number;
  color?: string;
};

const strokeAttrs = (size: number, color: string): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export function ArrowRight({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

export function ArrowUpRight({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

export function Play({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...rest}>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function Pause({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...rest}>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

export function CheckCircle2({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function Radio({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>
  );
}

export function MapPin({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function Search({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export function IndianRupee({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M6 3h12" />
      <path d="M6 8h12" />
      <path d="m6 13 8.5 8" />
      <path d="M6 13h3" />
      <path d="M9 13c6.667 0 6.667-10 0-10" />
    </svg>
  );
}

export function Move({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M5 9l-3 3 3 3" />
      <path d="M9 5l3-3 3 3" />
      <path d="M15 19l-3 3-3-3" />
      <path d="M19 9l3 3-3 3" />
      <path d="M2 12h20" />
      <path d="M12 2v20" />
    </svg>
  );
}

export function X({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

export function Clock({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

export function Star({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...rest}>
      <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.8 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
    </svg>
  );
}

export function ChevronLeft({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function ChevronRight({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function SlidersHorizontal({ size = 16, color = 'currentColor', ...rest }: IconProps) {
  return (
    <svg {...strokeAttrs(size, color)} {...rest}>
      <path d="M4 21v-7" />
      <path d="M4 10V3" />
      <path d="M12 21v-9" />
      <path d="M12 8V3" />
      <path d="M20 21v-5" />
      <path d="M20 12V3" />
      <path d="M2 14h4" />
      <path d="M10 8h4" />
      <path d="M18 16h4" />
    </svg>
  );
}
